import React, { useState, useEffect, useMemo } from "react";
import api from "services/api";
import { Helmet } from "react-helmet";
import { useParams, Link } from "react-router-dom";
import Translator, { __ } from "services/Translator";

import CategoryGames from "elements/gameElements/CategoryGames";
import GameMenu from "elements/gameElements/GameMenu";
import useCountryRefetch from "hooks/useCountryRefetch";
import useShowUnavailable from "hooks/useShowUnavailable";
import {
  filterGamesForDisplay,
  hasUnavailableGames,
} from "services/gameAvailability";
import {
  getProviderDisplayName,
  normalizeProviders,
  resolveProviderBySlug,
} from "utils/providers";

export default function ProviderGames(props) {
  const params = useParams();
  const slug = params.slug;

  const [providerId, setProviderId] = useState(null);
  const [providerName, setProviderName] = useState("");
  const [pagination, setPagination] = useState(null);
  const [games, setGames] = useState([]);
  const [gameSorting, setGameSorting] = useState(null);
  const [perPage, setPerPage] = useState(24);
  const [gameLoaded, setGameLoaded] = useState(0);
  const [totalGame, setTotalGame] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [isResolvingProvider, setIsResolvingProvider] = useState(true);
  // Bumps on a country switch so the feed refetches for the new scope/currency.
  const countryNonce = useCountryRefetch();
  const [showUnavailable, setShowUnavailableValue] = useShowUnavailable();

  const providers = useMemo(
    () => normalizeProviders(Array.isArray(props.providers) ? props.providers : []),
    [props.providers]
  );
  const providersLoading = Boolean(props.providersLoading);
  const providerFilter = providerId ? [providerId] : [];

  useEffect(() => {
    if (providersLoading) {
      setIsResolvingProvider(true);
      setNotFound(false);
      return;
    }

    const provider = resolveProviderBySlug(slug, providers);
    if (provider?.provider_id) {
      setProviderId(provider.provider_id);
      setProviderName(getProviderDisplayName(provider));
      setNotFound(false);
    } else {
      setProviderId(null);
      setProviderName("");
      setNotFound(true);
    }
    setIsResolvingProvider(false);
  }, [slug, providers, providersLoading]);

  const getGames = async (page) => {
    if (!providerId) return;
    props.setSpinner(true);
    const response = await api.post("/games/by-category", {
      per_page: perPage,
      game_types: ["all"],
      page: page,
      game_sorting: gameSorting,
      providers: [providerId],
    });
    props.setSpinner(false);
    if (response.status === 200) {
      const resGames = response.data.games || {};
      const resKey = Object.keys(resGames)[0] || "all";
      const resGamelist = resGames[resKey]?.gamelist ?? [];

      if (page === 1) {
        setGames(resGames);
      } else {
        const gamesClone = JSON.parse(JSON.stringify(games));
        const existingKey = Object.keys(gamesClone)[0] || resKey;
        const existingList = gamesClone[existingKey]?.gamelist ?? [];
        gamesClone[existingKey] = gamesClone[existingKey] || {};
        gamesClone[existingKey].gamelist = [...existingList, ...resGamelist];
        setGames(gamesClone);
      }
      setGameLoaded(
        page === 1
          ? response.data.loadedGameCount
          : gameLoaded + response.data.loadedGameCount
      );
      setTotalGame(response.data.gameCount);
      setPagination(response.data.paginationData || null);
    }
  };

  const onPageChange = (page) => {
    getGames(page);
  };

  useEffect(() => {
    if (providerId) {
      getGames(1);
    }
  }, [gameSorting, providerId, countryNonce]);

  const gameList =
    games.all?.gamelist ??
    games.video_slots?.gamelist ??
    (typeof games === "object" && games[Object.keys(games)[0]]?.gamelist) ??
    [];

  if (notFound && providers.length > 0) {
    return (
      <div className="container-xxl">
        <div className="providersContainer slideUpAnimation">
          <GameMenu {...props} />
          <div className="provider-not-found">
            <h2>{__("Provider not found")}</h2>
            <p>{__("The requested game provider could not be found.")}</p>
            <Link to="/providers" className="common_btn" hreflang={Translator.getSelectedLanguage?.()?.code}>
              {__("Browse all providers")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isResolvingProvider) {
    return (
      <div className="container-xxl">
        <div className="providersContainer slideUpAnimation">
          <GameMenu {...props} />
          <div className="providers-empty-state">{__("Loading provider...")}</div>
        </div>
      </div>
    );
  }

  if (!providerId) {
    return (
      <div className="container-xxl">
        <div className="providersContainer slideUpAnimation">
          <GameMenu {...props} />
          <div className="provider-not-found">
            <h2>{__("Provider unavailable")}</h2>
            <p>{__("Unable to load this provider right now. Please try another one.")}</p>
            <Link to="/providers" className="common_btn" hreflang={Translator.getSelectedLanguage?.()?.code}>
              {__("Browse all providers")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {providerName
            ? __("PC Play365 Casino | ") + providerName + __(" Games")
            : __("PC Play365 Casino | Provider Games")}
        </title>
        <meta
          name="description"
          content={
            providerName
              ? __(
                  "Play all "
                ) +
                providerName +
                __(
                  " games at PC Play365 Casino. Slots, table games and more from this top provider."
                )
              : __(
                  "Browse and play games from your favourite providers at PC Play365 Casino."
                )
          }
        />
      </Helmet>

      <div className="container-xxl">
        <div className="gamesContainer slideUpAnimation">
          <GameMenu {...props} />
          <div className="provider-games-back-row">
            <Link
              to="/providers"
              className="commonBtn pTwoBtn provider-games-back-btn"
              hreflang={Translator.getSelectedLanguage?.()?.code}
              aria-label={__("Go back to providers list")}
            >
              <i className="fas fa-arrow-left" aria-hidden="true"></i>
              <span>{__("Back to Providers")}</span>
            </Link>
          </div>
          <CategoryGames
            gameList={filterGamesForDisplay(gameList, showUnavailable)}
            showUnavailableToggle={hasUnavailableGames(gameList)}
            showUnavailable={showUnavailable}
            onToggleUnavailable={setShowUnavailableValue}
            pageHeadingText={
              providerName
                ? `${__("All Games")} - ${providerName}`
                : __("All Games")
            }
            categoryName={providerName || __("Provider Games")}
            isSearchResults={false}
            category="all"
            onPageChange={onPageChange}
            pagination={pagination}
            setGameSorting={setGameSorting}
            gameSorting={gameSorting}
            showPagination={true}
            gameLoaded={gameLoaded}
            totalGame={totalGame}
            providers={props.providers}
            selectedProviders={providerFilter}
            setSelectedProviders={() => {}}
            isAuthenticated={props.isAuthenticated}
            setSearchStr={props.setSearchStr}
            hideProviderFilter={true}
            {...props}
          />
        </div>
      </div>
    </>
  );
}
